import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiZap, FiAward, FiSmile, FiSettings, FiShield, FiClock } = FiIcons;

const stats = [
  { icon: FiSettings, value: 500,  suffix: '+',  label: 'Projects Completed', color: 'from-blue-500 to-cyan-500',    glow: 'rgba(0,102,255,0.4)'   },
  { icon: FiAward,    value: 13,   suffix: '+',  label: 'Years Experience',    color: 'from-yellow-500 to-amber-500', glow: 'rgba(245,158,11,0.4)'  },
  { icon: FiSmile,    value: 1000, suffix: '+',  label: 'Happy Customers',     color: 'from-green-500 to-teal-500',   glow: 'rgba(16,185,129,0.4)'  },
  { icon: FiZap,      value: 50,   suffix: '+',  label: 'Services Offered',    color: 'from-purple-500 to-pink-500',  glow: 'rgba(168,85,247,0.4)'  },
  { icon: FiShield,   value: 100,  suffix: '%',  label: 'Satisfaction Rate',   color: 'from-cyan-500 to-blue-500',    glow: 'rgba(6,182,212,0.4)'   },
  { icon: FiClock,    value: 24,   suffix: '/7', label: 'Emergency Service',   color: 'from-red-500 to-orange-500',   glow: 'rgba(239,68,68,0.4)'   },
];

const Counter = ({ value, suffix, duration = 1800 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = value / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, value, duration]);

  return (
    <span ref={ref} style={{ fontFamily: 'Georgia, serif' }}>
      {count}{suffix}
    </span>
  );
};

const Stats = () => {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-60px' });

  return (
    <section
      ref={sectionRef}
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #000000 0%, #020b1a 50%, #000000 100%)' }}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(0,102,255,0.4), rgba(0,204,255,0.4), transparent)' }}
      />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(0,102,255,0.4), rgba(0,204,255,0.4), transparent)' }}
      />

      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-10"
          style={{ background: 'radial-gradient(circle, #0066ff, transparent)' }} />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-10"
          style={{ background: 'radial-gradient(circle, #00ccff, transparent)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300 mb-4">
            <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
            <span>Our Track Record</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold">
            <span className="gradient-text">Numbers That</span>{' '}
            <span className="text-white">Speak For Us</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40, scale: 0.9 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="group relative flex flex-col items-center text-center"
            >
              <div
                className="relative w-full rounded-2xl p-6 border border-gray-800/60 hover:border-gray-600/60 transition-all duration-500 cursor-default"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    boxShadow: `0 0 30px ${stat.glow}`,
                    background: `radial-gradient(circle at center, ${stat.glow.replace('0.4', '0.06')}, transparent 70%)`
                  }}
                />
                <motion.div
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`}
                  style={{ boxShadow: `0 4px 20px ${stat.glow}` }}
                >
                  <SafeIcon icon={stat.icon} className="text-xl text-white" />
                </motion.div>
                <div
                  className="text-3xl md:text-4xl font-black mb-1 bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(135deg, #ffffff 0%, #94a3b8 100%)' }}
                >
                  <Counter value={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-xs text-gray-400 font-medium leading-tight tracking-wide">
                  {stat.label}
                </p>
                <div
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-0 group-hover:w-3/4 transition-all duration-500 rounded-full bg-gradient-to-r ${stat.color}`}
                />
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="text-center text-gray-600 text-sm mt-12 tracking-widest uppercase"
        >
          Serving Fatehgarh, Baramulla & across Jammu & Kashmir
        </motion.p>
      </div>
    </section>
  );
};

export default Stats;